import React, { useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import EDButton from '../../../../../../../BaseComponents/Buttons/Button/EDButton';
import EDColumns, {
  EDColumn,
} from '../../../../../../../BaseComponents/Columns/EDColumns';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import {
  TEDEKBokforderSTATUSEnum,
  TEDEKBokforderTYPEnum,
  TEDEKBokforderVariantEnum,
} from '../../../../../../../globalQueryTypes';
import {
  IBusinessCollectionProps,
  IObjektVy,
  IObjektVyComp,
} from '../../../../../../../Types/ObjektVy';
import Attribut from '../../../../../_Common/Components/BusinessCollectionComponents/Attribut/Attribut';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import ReadOnlyFormContextProvider from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/ReadOnlyFormContextProvider/ReadOnlyFormContextProvider';
import { CreateDialogOpenEnum } from '../../../../Rutiner/Bokforder/Components/BokforderProviderWrapper';
import { BokforderWrapperContext } from '../../../../Rutiner/Bokforder/Contexts/BokforderWrapperContext';
import VerifikationDialog from '../../../../Rutiner/EkonomiskAnalys/Components/VerifikationModal/VerifikationDialog';
import {
  BokforderType__Associations_RefBokfver__Associations_Handelser_All,
  BokforderType__Associations_RefBokfver__Associations_Handelser_All__Associations_Levfaktura__Associations_Fakturasidor_All,
} from '../../Queries/_QueryTypes/BokforderType';
import {
  getAttestantStrings,
  getBokforderCopyIDFromSearchParams,
  getBokforderTypeFromSearchParams,
} from '../../Utils/BokforderUtils';
import BokforderContext from '../BokforderContext/BokforderContext';
import { BokforderTemplateView } from '../BokforderTemplate/BokforderTemplate';
import KontrollattestantPicker from '../KontrollattestantPicker/KontrollattestantPicker';

interface IProps extends IBusinessCollectionProps {
  setCreateOptionDialogOpen: (
    value: React.SetStateAction<CreateDialogOpenEnum>
  ) => void;
}

const BokforderDetailInfoCardComp: IObjektVyComp = {
  caption: '',
  comp: props => <BokforderDetailInfoCardView {...(props as IProps)} />,
};

const BokforderDetailInfoCardView: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const formContext = useContext(BusinessDataFormContext);
  const bokforderContext = useContext(BokforderContext);
  const bokforderWrapperContext = useContext(BokforderWrapperContext);
  const location = useLocation();
  const [verDialogOpen, setVerDialogOpen] = useState<boolean>(false);

  const copyID = useMemo(
    () => getBokforderCopyIDFromSearchParams(location.search) ?? '',
    [location.search]
  );

  const type = getBokforderTypeFromSearchParams(location.search);

  const verificationCreated =
    formContext?.data?.STATUS === TEDEKBokforderSTATUSEnum.VERIFIKATION_SKAPAD;

  const isTemplateType =
    (bokforderContext.bokforderVariant === TEDEKBokforderVariantEnum.MALL ||
      formContext?.data?.MALL_ID != null) &&
    bokforderContext.isEditMode &&
    bokforderWrapperContext.createOptionDialogOpen ===
      CreateDialogOpenEnum.CLOSED;

  const TemplateComponent = (
    <>
      {formContext?.isNew && copyID === '' ? (
        <EDBox component="span">
          <BokforderTemplateView id={formContext?.data?.id ?? null} />
        </EDBox>
      ) : (
        <EDBox
          component="span"
          sx={theme => ({
            padding: `${theme.spacing(1)} ${theme.spacing(1)} 0`,
            display: 'block',
          })}
        >
          <EDTypography bold sx={{ display: 'inline-block' }}>
            Mall:&nbsp;
          </EDTypography>
          <EDTypography sx={{ display: 'inline-block' }}>
            {bokforderContext.currentTemplate?.BENAMNING}
          </EDTypography>
        </EDBox>
      )}
    </>
  );

  const verifikationID = useMemo(() => {
    return formContext?.formState.currentValues?._Associations?.RefBokfver?.id;
  }, [formContext?.formState.currentValues?._Associations?.RefBokfver?.id]);

  const hasUnderlag = useMemo(() => {
    const allUrpsrungUnderlag =
      formContext?.formState.currentValues?._Associations?.RefBokfver
        ?._Associations?.Bokforingsorder?._Associations?.Dokument_All;
    return formContext?.isNew
      ? bokforderContext.hasVerifikationUnderlag
      : allUrpsrungUnderlag?.length > 0;
  }, [
    bokforderContext.hasVerifikationUnderlag,
    formContext?.formState.currentValues?._Associations?.RefBokfver
      ?._Associations?.Bokforingsorder?._Associations?.Dokument_All,
    formContext?.isNew,
  ]);

  const hasFakturaBild = useMemo(() => {
    const allFakturasidor =
      formContext?.formState.currentValues?._Associations?.RefBokfver?._Associations?.Handelser_All?.reduce(
        (
          fakturasidor: BokforderType__Associations_RefBokfver__Associations_Handelser_All__Associations_Levfaktura__Associations_Fakturasidor_All[],
          handelse: BokforderType__Associations_RefBokfver__Associations_Handelser_All
        ) => {
          if (
            handelse._Associations?.Levfaktura?._Associations
              ?.Fakturasidor_All != null
          ) {
            return [
              ...fakturasidor,
              handelse._Associations?.Levfaktura?._Associations
                ?.Fakturasidor_All,
            ];
          } else {
            return fakturasidor;
          }
        },
        []
      );

    return formContext?.isNew
      ? bokforderContext.hasVerifikationFakturabild
      : allFakturasidor?.length > 0;
  }, [
    bokforderContext.hasVerifikationFakturabild,
    formContext?.formState.currentValues?._Associations?.RefBokfver
      ?._Associations?.Handelser_All,
    formContext?.isNew,
  ]);

  const showOpenUrsprungsverBtn = useMemo(() => {
    return (
      (bokforderContext.bokforderVariant ===
        TEDEKBokforderVariantEnum.OMBOKFOR ||
        bokforderContext.bokforderVariant === TEDEKBokforderVariantEnum.VAND) &&
      (hasUnderlag || hasFakturaBild) &&
      verifikationID
    );
  }, [
    bokforderContext.bokforderVariant,
    hasFakturaBild,
    hasUnderlag,
    verifikationID,
  ]);

  const onUrsprungsverBtnClick = () => {
    setVerDialogOpen(true);
  };

  return (
    <>
      <ReadOnlyFormContextProvider disabled={false}>
        <EDBox
          sx={{
            width: theme => `calc(100% - ${theme.spacing(2)})`,
            height: 'auto',
            paddingX: theme => theme.spacing(1),
          }}
        >
          <EDColumns spacing={0}>
            <EDColumn md={3}>
              <Attribut name="STATUS" size="large" />
              <Attribut name="BESTALLNINGSNR" size="large" />
              {verificationCreated && <Attribut name="VER_NR" size="large" />}
            </EDColumn>
            <EDColumn md={3}>
              <Attribut
                name="BestalldNamnDatum"
                customDisplayLabel="Beställare"
                size="large"
              />
              <EDBox
                sx={theme => ({
                  padding: `${theme.spacing(1)} ${theme.spacing(1)} 0`,
                  display: 'flex',
                  flexFlow: 'row wrap',
                })}
              >
                <EDTypography bold sx={{ display: 'inline-block' }}>
                  Attestanter:&nbsp;
                </EDTypography>

                {getAttestantStrings(
                  formContext?.data?._Associations?.Rader ?? [],
                  formContext?.data?.TYP
                )?.map(attestantText => (
                  <EDTypography
                    key={attestantText}
                    component="span"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {attestantText}&nbsp;
                  </EDTypography>
                ))}
              </EDBox>
              {verificationCreated && (
                <Attribut
                  name="BokfordNamnDatum"
                  customDisplayLabel="Bokförd av"
                  size="large"
                />
              )}
            </EDColumn>
            <EDColumn md={3}>
              {(formContext?.formState.currentValues?.TYP ===
                TEDEKBokforderTYPEnum.UTANORDNING ||
                type === TEDEKBokforderTYPEnum.UTANORDNING) && (
                <KontrollattestantPicker isDisabled={false} />
              )}
            </EDColumn>
            <EDColumn md={3}>
              {isTemplateType && !showOpenUrsprungsverBtn && TemplateComponent}
              {!isTemplateType && showOpenUrsprungsverBtn && (
                <EDBox
                  sx={theme => ({
                    paddingRight: `${theme.spacing(3)}`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  })}
                >
                  <EDButton
                    label={'Bilagor'}
                    flat
                    onClick={onUrsprungsverBtnClick}
                  />
                </EDBox>
              )}
            </EDColumn>
          </EDColumns>
        </EDBox>
        {showOpenUrsprungsverBtn && (
          <VerifikationDialog
            canViewFullVerifikationInfo
            open={verDialogOpen}
            onClose={() => setVerDialogOpen(false)}
            verifikationId={verifikationID}
            tab={hasUnderlag ? 'underlag' : 'fakturabild'}
            showOnlyVerificationAttachments
          />
        )}
      </ReadOnlyFormContextProvider>
    </>
  );
};

export default BokforderDetailInfoCardView;

export const BokforderDetailInfoCard: IObjektVy = {
  type: 'ObjektVy',
  id: 'BokforderDetailInfoCard',
  info: {},
  def: () => BokforderDetailInfoCardComp,
};
